import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * FilteringTips Component
 * Shows a collapsible panel explaining how content is filtered during export
 */
const FilteringTips = () => {
  const [showTips, setShowTips] = useState(false);

  return (
    <div className="mt-2 mb-4 text-center">
      <button
        onClick={() => setShowTips(!showTips)}
        className="text-sm text-blue-500 hover:underline flex items-center justify-center mx-auto"
      >
        <span>Filtering rules</span>
        <ChevronDown
          className={`h-4 w-4 ml-1 transition-transform ${showTips ? 'rotate-180' : ''}`}
        />
      </button>

      {showTips && (
        <div className="mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 text-left text-sm animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="font-medium">Skip Categories</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Mark content to control visibility:
              </div>
              <ul className="text-xs text-gray-500 dark:text-gray-400 mt-1 space-y-1 ml-3">
                <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">manual</code> - Manually skipped</li>
                <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">image_only</code> - Minimal text, mostly images</li>
                <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">low_quality</code> - Low engagement/promotional</li>
                <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">long_text</code> - Very long content (&gt;500 chars)</li>
                <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">spam</code> - Spam content</li>
                <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">duplicate</code> - Retweets/duplicates</li>
              </ul>
            </div>

            <div>
              <div className="font-medium">Export Filtering</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                When exporting to JSON files:
              </div>
              <div className="text-xs mt-2">
                <span className="font-medium text-red-500 dark:text-red-400">Removed from JSON:</span>
                <ul className="text-gray-500 dark:text-gray-400 mt-1 space-y-0.5 ml-3">
                  <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">spam</code></li>
                  <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">duplicate</code></li>
                </ul>
              </div>
              <div className="text-xs mt-2">
                <span className="font-medium text-green-500 dark:text-green-400">Kept in JSON:</span>
                <ul className="text-gray-500 dark:text-gray-400 mt-1 space-y-0.5 ml-3">
                  <li>All other categories (hidden via UI)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="font-medium text-xs">UI Visibility</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Dev mode:</span> Toggle "Hide skipped" to show/hide all skip categories
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Production:</span> Only <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">manual</code> skips are hidden
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="font-medium text-xs">Platform-Specific Filtering</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span className="font-medium">ESP32 &amp; TRMNL (e-ink):</span> Automatically excludes <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">image_only</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">low_quality</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">long_text</code>, and <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">manual</code> categories (optimized for e-ink displays)
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="font-medium text-xs">Additional Rules</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span className="font-medium">Roam quotes:</span> Items tagged "new word" are filtered out during export
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span className="font-medium">Auto un-skip:</span> Roam quotes only marked as <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">image_only</code> or <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">long_text</code> are automatically un-skipped (web can handle these)
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilteringTips;
